// Button.js
// Shared button component with hover and pressed state handling.
// Use this for all buttons throughout the application.
//
// Props:
//   variant   — 'primary' | 'secondary' | 'danger' | 'ghost' | 'neutral' | 'caution'
//   size      — 'sm' | 'md' (default: 'md')
//   disabled  — bool
//   onClick   — function
//   type      — 'button' | 'submit' (default: 'button')
//   style     — optional override styles
//   children  — button label content

import React, { useState } from 'react';

const VARIANTS = {
  primary: {
    base:    { backgroundColor: 'var(--color-primary)', color: '#ffffff', border: 'none' },
    hover:   { backgroundColor: 'var(--color-nav-hover)' },
    pressed: { backgroundColor: 'var(--color-nav-pressed)' },
  },
  secondary: {
    base:    { backgroundColor: 'transparent', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderColor: 'var(--color-border)' },
    hover:   { backgroundColor: 'var(--color-accent-wash)', borderColor: 'var(--color-accent-tint)' },
    pressed: { backgroundColor: 'var(--color-accent-tint)', borderColor: 'var(--color-accent-tint)' },
  },
  danger: {
    base:    { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderColor: '#fca5a5' },
    hover:   { backgroundColor: '#fecaca', borderColor: '#fca5a5' },
    pressed: { backgroundColor: '#fca5a5', borderColor: '#fca5a5' },
  },
  ghost: {
    base:    { backgroundColor: 'transparent', color: 'var(--color-primary)', border: 'none' },
    hover:   { backgroundColor: 'var(--color-accent-wash)' },
    pressed: { backgroundColor: 'var(--color-accent-tint)' },
  },
  // Neutral — subtle gray, used for table row actions (Edit, etc.)
  neutral: {
    base:    { backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderColor: 'var(--color-border)' },
    hover:   { backgroundColor: 'var(--color-accent-wash)', borderColor: 'var(--color-accent-tint)' },
    pressed: { backgroundColor: 'var(--color-accent-tint)', borderColor: 'var(--color-accent-tint)' },
  },
  // Caution — amber, used for reversible but impactful actions (Deactivate, Revoke MFA)
  caution: {
    base:    { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderColor: '#f59e0b' },
    hover:   { backgroundColor: '#fde68a', borderColor: '#f59e0b' },
    pressed: { backgroundColor: '#fcd34d', borderColor: '#f59e0b' },
  },
};

const SIZES = {
  sm: { padding: '5px 10px', fontSize: 'var(--font-size-xs)' },
  md: { padding: '7px 14px', fontSize: 'var(--font-size-sm)' },
};

export default function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  type = 'button',
  style: overrideStyle,
  children,
  ...rest
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.md;

  const computedStyle = {
    // Base shape and typography — never changes
    ...s,
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-family-base)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    lineHeight: 'var(--line-height-tight)',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'background-color var(--transition-fast), border-color var(--transition-fast)',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    // Variant base
    ...v.base,
    // State overrides
    ...(hovered && !pressed && !disabled ? v.hover : {}),
    ...(pressed && !disabled ? v.pressed : {}),
    // Disabled state
    ...(disabled ? { opacity: 0.5 } : {}),
    // Caller overrides — applied last
    ...overrideStyle,
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={computedStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      {...rest}
    >
      {children}
    </button>
  );
}